import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { useModal } from "hooks/useModal";
import { ModalProps } from "types/utils/modal";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import { ModalDescriptionWrapper } from "pages/Books/RevenueMetrics/RevenueMetricsLocked";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useBulkAssignClassificationMutation } from "store/apis/classifications";
import { useState } from "react";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { ClassificationAssignment } from "components/ClassificationAssignment/ClassificationAssignment";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { pluralize } from "utils/pluralize";

type BulkClassificationProps = ModalProps & {
  deselectRows: () => void;
  allSelectedRows: { uuid: string }[];
};

export const BulkClassification = ({
  close,
  isOpen,
  deselectRows,
  allSelectedRows,
}: BulkClassificationProps) => {
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);

  const [bulkAssignClassification, { isLoading: isAssigning }] =
    useBulkAssignClassificationMutation();

  const handleAssignClass = (
    classUuid: string,
    _groupUuid: string,
    existingClassToRemove?: string
  ) => {
    let newClassIds = [...selectedClassIds];

    if (existingClassToRemove) {
      newClassIds = newClassIds.filter((id) => id !== existingClassToRemove);
    }

    if (!newClassIds.includes(classUuid)) {
      newClassIds.push(classUuid);
    }

    setSelectedClassIds(newClassIds);
  };

  const handleRemoveClass = (classUuid: string) => {
    setSelectedClassIds(selectedClassIds.filter((id) => id !== classUuid));
  };

  const handleConfirm = async () => {
    const transactionIds = allSelectedRows.map((row) => row.uuid);

    try {
      await bulkAssignClassification({
        entityId,
        payload: {
          class_ids: selectedClassIds,
          transaction_ids: transactionIds,
        },
      }).unwrap();

      successToast({
        title: "Classifications assigned successfully",
        message: `For ${pluralize(
          transactionIds.length,
          "transaction",
          "transactions"
        )}`,
      });
      deselectRows();
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleClose = () => {
    setSelectedClassIds([]);
    close();
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={handleClose}>
        <Modal.Content>
          <Modal.Header className="sm:t-pl-4">
            <Modal.Title>Classification</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="sm:t-px-4">
            <ClassificationAssignment
              entityId={entityId}
              required
              assignedClassificationIds={selectedClassIds}
              onAssignClass={handleAssignClass}
              onRemoveClass={handleRemoveClass}
              label="Assign Classification"
              maxChips={2}
              blockedUI={
                <Combobox
                  label={
                    <span className="t-flex t-items-center t-gap-1">
                      Assign Classification{" "}
                      <DiamondPurple height={12} width={12} />
                    </span>
                  }
                  placeholder="Select"
                  menuPortalTarget={document.body}
                  isDisabled
                  value={[]}
                  options={[]}
                />
              }
            />
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={handleClose} disabled={isAssigning}>
              Cancel
            </Button>
            <Button
              customType="primary"
              onClick={handleConfirm}
              isLoading={isAssigning}
              disabled={isAssigning || selectedClassIds.length === 0}
            >
              Confirm
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
